import { UiIcon } from '@/components/ui/UiIcon';
import type { ContactDataDto, SocialLinkDto } from '@/lib/sanity/dto';
import { stegaClean } from '@/lib/utils/stega';
import { cn } from '@/lib/utils/tw-merge';

/** Platform name for the accessible label, since the icon carries no text. */
const SOCIAL_LABELS: Record<string, string> = {
    instagram: 'Instagram',
    linkedin: 'LinkedIn',
    x: 'X',
    facebook: 'Facebook',
    github: 'GitHub',
    youtube: 'YouTube',
    vimeo: 'Vimeo',
    tiktok: 'TikTok',
    pinterest: 'Pinterest',
    whatsapp: 'WhatsApp',
    telegram: 'Telegram',
    mastodon: 'Mastodon',
};

type ContactDetailsProps = {
    contact?: ContactDataDto;
    socialLinks?: SocialLinkDto[];
    /** Small label above the social row, e.g. "Folgen". */
    socialLabel?: string;
    className?: string;
};

/**
 * How to reach the studio: number, address, and the accounts. One component for
 * both places the design shows it — the footer column and the contact page's
 * closing block — because it is one set of facts, and a phone number that
 * differs between the two would be a bug nobody notices.
 *
 * Presentational on purpose: the caller supplies the data, so the footer (which
 * already holds the settings) does not fetch them twice.
 */
export function ContactDetails({ contact, socialLinks = [], socialLabel, className }: ContactDetailsProps) {
    const phone = contact?.contactPhone?.trim();
    // A tel: href is a value, not rendered text — stega would break the dial.
    const phoneHref = phone ? `tel:${stegaClean(phone).replace(/[^+\d]/g, '')}` : undefined;
    const email = contact?.contactEmail?.trim();
    const street = contact?.contactStreet?.trim();
    const city = [contact?.contactZip, contact?.contactCity].filter(Boolean).join(' ').trim();

    if (!phoneHref && !email && !street && !city && !socialLinks.length) return null;

    return (
        <div className={cn('contact-details', className)}>
            {phoneHref ? (
                <a href={phoneHref} className="contact-details__phone">
                    {phone}
                </a>
            ) : null}

            {email ? (
                <a href={`mailto:${stegaClean(email)}`} className="contact-details__mail">
                    {email}
                </a>
            ) : null}

            {street || city ? (
                <address className="contact-details__address">
                    {street}
                    {street && city ? <br /> : null}
                    {city}
                </address>
            ) : null}

            {socialLinks.length ? (
                <div className="contact-details__social">
                    {socialLabel ? <span className="contact-details__social-head">{socialLabel}</span> : null}

                    <div className="contact-details__social-row">
                        {socialLinks.map((social) => {
                            const label = SOCIAL_LABELS[stegaClean(social.platform)] ?? social.platform;

                            return (
                                <a
                                    key={social.url}
                                    href={social.url}
                                    className="contact-details__social-link"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    aria-label={label}
                                >
                                    <span className="contact-details__social-icon">
                                        <UiIcon name={social.platform} aria-hidden="true" />
                                    </span>
                                    <span>{label}</span>
                                </a>
                            );
                        })}
                    </div>
                </div>
            ) : null}
        </div>
    );
}
